import { compileStyles, createMediaStyles } from 'lib/styled-helpers'
import React, { FC } from 'react'
import styled from 'styled-components'

import { multiplyRems } from '../../application/theme/utils'
import { getProjectName } from '../../lib/get-project-name'
import { Tooltip } from '../tooltip'

export type Props = {
  checked?: boolean
  label?: string | React.ReactElement
  tooltip?: string
  disabled?: boolean
  onChange(newValue: boolean): void
  size?: 'medium' | 'big'
  mobileSpaceBetween?: boolean
  name?: string
}

export const Switch: FC<Props> = ({
  checked = false,
  label,
  tooltip,
  onChange,
  disabled = false,
  size = 'medium',
  mobileSpaceBetween = false,
  name
}) => {
  return (
    <Label>
      <Input type="checkbox" onChange={() => onChange(!checked)} checked={checked} disabled={disabled} name={name} />
      <SwitchContent mobileSpaceBetween={mobileSpaceBetween}>
        <SwitchField checked={checked} size={size} mobileSpaceBetween={mobileSpaceBetween}></SwitchField>
        {typeof label === 'string' ? (
          <SwitchText data-for="label" data-tip={tooltip}>
            {label}
          </SwitchText>
        ) : (
          label
        )}
        <Tooltip id="label" />
        <Tooltip id="label" multiline={true} />
      </SwitchContent>
    </Label>
  )
}

const Label = styled.label`
  display: inline-flex;
  cursor: pointer;
`

const Input = styled.input`
  position: absolute;
  top: 0;
  left: 0;
  z-index: 1;
  display: none;
  width: 100%;
  height: 100%;
`

const SwitchContent = styled.div<{ mobileSpaceBetween: boolean }>`
  display: flex;
  align-items: center;
  width: 100%;

  ${({ mobileSpaceBetween }) =>
    createMediaStyles(() => ({
      phone: mobileSpaceBetween ? 'flex-direction: row-reverse; justify-content: space-between;' : '',
      tablet: 'flex-direction: row; justify-content: stretch;'
    }))}

  p {
    color: ${({ theme }) => {
      if (getProjectName() === 'tank') return theme.palette.grey[500]
      return
    }};
  }
`

const SwitchField = styled.div<{ checked: boolean; size: 'medium' | 'big'; mobileSpaceBetween: boolean }>`
  ${({ size, theme }) =>
    size === 'medium'
      ? `
  width: ${theme.spacing[8]};
  height: ${theme.spacing[5]};
  border-radius: ${theme.spacing[3]};
  `
      : `
  width: ${theme.spacing[10]};
  height: ${theme.spacing[6]};
  border-radius: ${theme.spacing[3]};
  `};

  position: relative;
  background: ${({ checked, theme }) => {
    if (getProjectName() === 'tank') return checked ? theme.palette.brand.primary : '#4A4A4A'
    if (getProjectName() === 'generic_v2') return checked ? '#828282' : '#2C2C2C'
    return checked ? theme.palette.brand.primary : theme.palette.grey[300]
  }};
  border: ${() => {
    if (getProjectName() === 'generic_v2') return '0.5px solid #828282'
    return 'none'
  }};
  flex-shrink: 0;

  :before {
    ${({ size, checked, theme }) => {
      let topSpacing = multiplyRems(theme.spacing[1], 0.5)
      if (getProjectName() === 'generic_v2') {
        topSpacing = '0.1rem'
      }

      return size === 'medium'
        ? `
    top: ${topSpacing};
    left: ${multiplyRems(theme.spacing[1], 0.5)};
    width: ${theme.spacing[4]};
    height: ${theme.spacing[4]};
    transform: ${checked ? `translateX(${theme.spacing[3]})` : `translateX(${theme.spacing[0]})`};
  `
        : `
    top: ${getProjectName() === 'generic_v2' ? '1px' : multiplyRems(theme.spacing[1], 0.5)};
    left: ${multiplyRems(theme.spacing[1], 0.5)};
    width: ${theme.spacing[5]};
    height: ${theme.spacing[5]};
    transform: ${checked ? `translateX(${theme.spacing[4]})` : `translateX(${theme.spacing[0]})`};
  `
    }}
    content: '';
    position: absolute;
    border-radius: 50%;
    background: ${({ theme }) => {
      if (getProjectName() === 'tank') return '#2C3232'
      if (getProjectName() === 'generic_v2') return '#d9d9d9'
      return theme.palette.white
    }};
    transition: transform 0.25s;
  }

  ${({ mobileSpaceBetween }) =>
    createMediaStyles(() => ({
      phone: mobileSpaceBetween ? 'margin-right: 0;' : 'margin-right: 1.6rem',
      tablet: 'flex-direction: row; justify-content: stretch; margin-right: 1.6rem;'
    }))}
`

const SwitchText = styled.span`
  user-select: none;

  ${({ theme }) => {
    return `
      color: ${getProjectName() === 'tank' ? 'white' : theme.palette.grey[900]};
      ${compileStyles(theme.textStyles.p2)}
    `
  }};

  ${({ theme }) =>
    getProjectName() === 'generic_v2' &&
    `
      color: #D9D9D9;

      font-size: 12px;

      @media (min-width: 1024px) {
        font-size: 14px;
      }
  `}
`
